import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getMyGhlAccount,
  saveMyGhlAccount,
  disconnectMyGhlAccount,
  listGhlSocialAccounts,
} from "@/lib/ghl-connect.functions";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldCheck, AlertTriangle, Unplug, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/admin/social/connect-ghl")({
  component: ConnectGhlPage,
});

function ConnectGhlPage() {
  const getAccount = useServerFn(getMyGhlAccount);
  const save = useServerFn(saveMyGhlAccount);
  const disconnect = useServerFn(disconnectMyGhlAccount);
  const listAccts = useServerFn(listGhlSocialAccounts);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["my-ghl-account"],
    queryFn: () => getAccount(),
  });
  const accountsQ = useQuery({
    queryKey: ["ghl-social-accounts"],
    queryFn: () => listAccts(),
    enabled: !!data?.account?.is_active,
  });

  const [locationId, setLocationId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [label, setLabel] = useState("");
  const [ghlUserId, setGhlUserId] = useState("");
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (data?.account) {
      setLocationId(data.account.location_id ?? "");
      setLabel(data.account.label ?? "");
      setGhlUserId((data.account as any).ghl_user_id ?? "");
    }
  }, [data?.account]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!locationId.trim() || !apiKey.trim()) {
      toast.error("Location ID and Private Integration Token are required");
      return;
    }
    setSaving(true);
    try {
      const res = await save({
        data: {
          location_id: locationId.trim(),
          api_key: apiKey.trim(),
          label: label.trim() || undefined,
          ghl_user_id: ghlUserId.trim() || undefined,
        },
      });
      if (res.ok) {
        toast.success("GoHighLevel connected — credentials verified.");
        setApiKey("");
      } else {
        toast.error(res.error ?? "Saved, but the connection test failed. Double-check the token.");
      }
      await refetch();
    } catch (err: any) {
      toast.error(err?.message ?? "Could not save credentials");
    } finally {
      setSaving(false);
    }
  }

  async function onDisconnect() {
    if (!confirm("Disconnect this GHL account? You'll need to paste a new token to reconnect.")) return;
    setDisconnecting(true);
    try {
      await disconnect();
      setApiKey("");
      setLocationId("");
      setLabel("");
      setGhlUserId("");
      toast.success("Disconnected");
      await refetch();
    } catch (err: any) {
      toast.error(err?.message ?? "Could not disconnect");
    } finally {
      setDisconnecting(false);
    }
  }

  const account = data?.account;
  const connected = !!account && account.last_test_ok;

  return (
    <div>
      <SocialSubNav />
      <div className="max-w-4xl space-y-6">
        <header>
          <h1 className="font-display text-2xl font-bold">Connect GoHighLevel</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Paste your GHL sub-account's Private Integration Token to publish and pull analytics for
            your connected social accounts.
          </p>
        </header>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Connection status
              </CardTitle>
              <CardDescription>
                {isLoading
                  ? "Loading…"
                  : connected
                    ? "Verified and active."
                    : account
                      ? "Saved, but the last test failed."
                      : "Not connected yet."}
              </CardDescription>
            </div>
            {connected ? (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Connected</Badge>
            ) : account ? (
              <Badge variant="destructive">Error</Badge>
            ) : (
              <Badge variant="secondary">Disconnected</Badge>
            )}
          </CardHeader>
          {account && (
            <CardContent className="text-sm space-y-1">
              <div><span className="text-muted-foreground">Location ID:</span> <span className="font-mono">{account.location_id}</span></div>
              {account.label && <div><span className="text-muted-foreground">Label:</span> {account.label}</div>}
              {(account as any).ghl_user_id && (
                <div><span className="text-muted-foreground">GHL User ID:</span> <span className="font-mono">{(account as any).ghl_user_id}</span></div>
              )}
              <div><span className="text-muted-foreground">Last test:</span> {account.last_test_at ? new Date(account.last_test_at).toLocaleString() : "never"}</div>
              {!account.last_test_ok && account.last_test_error && (
                <div className="mt-2 flex items-start gap-2 rounded-md bg-destructive/10 text-destructive p-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{account.last_test_error}</span>
                </div>
              )}
              <div className="pt-3">
                <Button variant="outline" onClick={onDisconnect} disabled={disconnecting}>
                  {disconnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unplug className="w-4 h-4 mr-2" />}
                  Disconnect
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {connected && (
          <Card>
            <CardHeader>
              <CardTitle>Connected social accounts</CardTitle>
              <CardDescription>
                Pulled live from your GoHighLevel Social Planner. Connect additional networks inside GHL.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {accountsQ.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading accounts…</p>
              ) : !accountsQ.data?.ok ? (
                <p className="text-sm text-destructive">{(accountsQ.data as any)?.error ?? "Couldn't load accounts."}</p>
              ) : accountsQ.data.accounts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No social accounts connected in GHL yet. Open Social Planner in GHL and link Facebook, Instagram, LinkedIn, X, TikTok, YouTube, or Google Business.
                </p>
              ) : (
                <ul className="grid gap-2 sm:grid-cols-2">
                  {accountsQ.data.accounts.map((a: any) => (
                    <li key={a.id} className="flex items-center gap-3 rounded-lg border border-border bg-background p-3">
                      {a.avatar_url ? (
                        <img src={a.avatar_url} alt="" className="h-8 w-8 rounded-full" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-muted" />
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{a.name}</p>
                        <p className="text-xs capitalize text-muted-foreground">{a.platform}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{account ? "Update credentials" : "Connect your sub-account"}</CardTitle>
            <CardDescription>
              Paste a Private Integration Token from your GHL sub-account. We test it before saving.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="locationId">GHL Location ID *</Label>
                <Input
                  id="locationId"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  placeholder="ve9EPM428h8vShlRW1KT"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="apiKey">Private Integration Token *</Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={account ? "Paste a new token to rotate" : "pit-..."}
                  required={!account}
                />
                <p className="text-xs text-muted-foreground">
                  Stored server-side. Only this admin account can read it back.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="label">Friendly label (optional)</Label>
                  <Input
                    id="label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Simply Approved Mortgages – Main"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ghlUserId">GHL User ID (required to publish)</Label>
                  <Input
                    id="ghlUserId"
                    value={ghlUserId}
                    onChange={(e) => setGhlUserId(e.target.value)}
                    placeholder="GHL user ID that owns the social accounts"
                  />
                </div>
              </div>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                {account ? "Update & re-test" : "Connect & test"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>How to get these values</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-3">
            <ol className="list-decimal pl-5 space-y-2">
              <li>In GHL, switch into the sub-account you want connected.</li>
              <li>Open <strong>Settings → Business Profile</strong> and copy the <strong>Location ID</strong>.</li>
              <li>Open <strong>Settings → Private Integrations → Create new</strong>.</li>
              <li>
                Enable scopes: <code>social-planner.readonly</code>, <code>social-planner.write</code>,
                <code>locations.readonly</code>, <code>medias.readonly</code>, <code>medias.write</code>.
              </li>
              <li>Copy the long token and paste it above.</li>
            </ol>
            <a
              href="https://help.gohighlevel.com/support/solutions/articles/155000003054-private-integrations-tokens"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              GHL docs <ExternalLink className="w-3 h-3" />
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
